import z from "zod";
import {
    TEMPLATE_ELIGIBLE_TASK_TYPES,
    getTaskTypeLabel,
} from "@common/constants/taskTypeDefinitions";
import { TaskType } from "@common/types/task";
import { AccrualSuggestionBucket } from "@common/types/accrualSuggestionBucket";
import {
    TaskTemplateAssigneeRole,
    TaskTemplateDeadlineAnchor,
} from "@common/types/taskTemplate";
import type { TransactionsViewType } from "./closeTemplateTaskViewConfig";
import {
    ColumnSchema,
    View,
    ViewDensity,
    ViewSchema,
    ViewType,
} from "@common/types/views/views";
import { CloseTemplateTaskColumn } from "./closeTemplateTasksViewConfig";

export const NO_STAGE_VALUE = "__no_stage__";

export const BaseTaskFormSchema = z.object({
    name: z.string().min(1, { message: "Required" }).max(255),
    closeStageId: z.string().nullable(),
    assigneeRole: z.nativeEnum(TaskTemplateAssigneeRole).nullable(),
    externalDeadline: z.number().int().min(0).max(365).nullable(),
    deadlineAnchor: z.nativeEnum(TaskTemplateDeadlineAnchor).nullable(),
    estimatedTime: z.number().int().min(0).nullable(),
    initialMessage: z.string().max(50000).nullable(),
});

export const DEADLINE_ANCHOR_LABEL: Record<TaskTemplateDeadlineAnchor, string> =
    {
        [TaskTemplateDeadlineAnchor.TODAY]: "From today",
        [TaskTemplateDeadlineAnchor.START_DATE]: "From period start date",
        [TaskTemplateDeadlineAnchor.END_DATE]: "From period end date",
        [TaskTemplateDeadlineAnchor.WORK_START_DATE]: "From work start date",
    };

export const DEADLINE_ANCHOR_OPTIONS: TaskTemplateDeadlineAnchor[] = [
    TaskTemplateDeadlineAnchor.TODAY,
    TaskTemplateDeadlineAnchor.START_DATE,
    TaskTemplateDeadlineAnchor.END_DATE,
    TaskTemplateDeadlineAnchor.WORK_START_DATE,
];

/**
 * The anchor a deadline offset is measured from when no explicit anchor is set.
 * Mirrors the backend default in `close-template-task.helpers.ts`
 * (`computeDueDate`), which resolves a null anchor to the work start date.
 */
export const DEFAULT_DEADLINE_ANCHOR =
    TaskTemplateDeadlineAnchor.WORK_START_DATE;

const DEADLINE_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);

export const formatDeadlineLabel = (days: number): string => `+ ${days}d`;

/**
 * Header shown above the deadline offset options, describing which date the
 * offset is counted from. Derived from the (possibly null) anchor so it stays
 * accurate as the anchor changes.
 */
export const formatDeadlineHeaderLabel = (
    anchor: TaskTemplateDeadlineAnchor | null,
): string =>
    `Days ${DEADLINE_ANCHOR_LABEL[anchor ?? DEFAULT_DEADLINE_ANCHOR].toLowerCase()}`;

/**
 * Returns deadline options, including a legacy value if it falls outside 1-30.
 * This ensures existing task templates with deadlines > 30 remain visible and
 * selectable in the dropdown.
 */
export const getDeadlineOptions = (currentValue: number | null): number[] => {
    if (currentValue === null || (currentValue >= 1 && currentValue <= 30)) {
        return DEADLINE_OPTIONS;
    }

    return [...DEADLINE_OPTIONS, currentValue].sort((a, b) => a - b);
};

/** Default task title applied when the task type is changed to Transaction Review. */
const TRANSACTION_REVIEW_DEFAULT_TITLE = "Transactions review";

/** Default task title applied when the Uncategorized review preset is selected. */
const UNCATEGORIZED_REVIEW_DEFAULT_TITLE = "Uncategorized transaction review";

/** Default task title applied when the task type is changed to 1099 review. */
const FORM_1099_DEFAULT_TITLE = "1099 review";

/**
 * Default initial chat message applied when the task type is changed to Transaction
 * Review. Stored as a serialized Slate value (JSON string).
 */
const TRANSACTION_REVIEW_DEFAULT_MESSAGE = JSON.stringify([
    {
        type: "paragraph",
        children: [
            {
                text: "Please review the following transactions and flag any that need attention.",
            },
        ],
    },
]);

/**
 * Returns default title and initialMessage to pre-populate when the task type
 * changes. TRANSACTION_REVIEW, the Uncategorized review preset, and FORM_1099
 * have defaults; all other types return nulls.
 */
export function getTaskTypeDefaults(type: TaskTypeOption): {
    name: string | null;
    initialMessage: string | null;
} {
    if (type === TRANSACTION_PRESET_UNCATEGORIZED) {
        return {
            name: UNCATEGORIZED_REVIEW_DEFAULT_TITLE,
            initialMessage: TRANSACTION_REVIEW_DEFAULT_MESSAGE,
        };
    }
    if (type === TaskType.TRANSACTION_REVIEW) {
        return {
            name: TRANSACTION_REVIEW_DEFAULT_TITLE,
            initialMessage: TRANSACTION_REVIEW_DEFAULT_MESSAGE,
        };
    }
    if (type === TaskType.FORM_1099) {
        return {
            name: FORM_1099_DEFAULT_TITLE,
            initialMessage: null,
        };
    }
    return { name: null, initialMessage: null };
}

// ---------------------------------------------------------------------------
// Transaction preset identifiers
// These are NOT new TaskType values. Uncategorized resolves to
// TaskType.TRANSACTION_REVIEW; the rest resolve to TaskType.TRANSACTIONS
// with pre-set filters applied to the viewConfig.
// ---------------------------------------------------------------------------

export const TRANSACTION_PRESET_WITHOUT_COUNTERPARTY =
    "transactions_without_counterparty" as const;
export const TRANSACTION_PRESET_UNCATEGORIZED =
    "uncategorized_transactions" as const;

export type TransactionPreset =
    | typeof TRANSACTION_PRESET_WITHOUT_COUNTERPARTY
    | typeof TRANSACTION_PRESET_UNCATEGORIZED;

const TRANSACTION_PRESETS: readonly TransactionPreset[] = [
    TRANSACTION_PRESET_WITHOUT_COUNTERPARTY,
    TRANSACTION_PRESET_UNCATEGORIZED,
] as const;

export function isTransactionPreset(value: string): value is TransactionPreset {
    return TRANSACTION_PRESETS.some((p) => p === value);
}

// ---------------------------------------------------------------------------
// Accrual review preset identifiers
// These resolve to TaskType.ACCRUAL_REVIEW with a bucket-specific viewConfig.
// One preset per AccrualSuggestionBucket value.
// ---------------------------------------------------------------------------

const ACCRUAL_REVIEW_PRESET_RULE_SUGGESTED =
    "accrual_review_rule_suggested" as const;
const ACCRUAL_REVIEW_PRESET_INVOICE = "accrual_review_invoice" as const;
const ACCRUAL_REVIEW_PRESET_BILL = "accrual_review_bill" as const;
const ACCRUAL_REVIEW_PRESET_TRANSFER = "accrual_review_transfer" as const;
const ACCRUAL_REVIEW_PRESET_OTHER = "accrual_review_other" as const;

export type AccrualReviewPreset =
    | typeof ACCRUAL_REVIEW_PRESET_RULE_SUGGESTED
    | typeof ACCRUAL_REVIEW_PRESET_INVOICE
    | typeof ACCRUAL_REVIEW_PRESET_BILL
    | typeof ACCRUAL_REVIEW_PRESET_TRANSFER
    | typeof ACCRUAL_REVIEW_PRESET_OTHER;

const ACCRUAL_REVIEW_PRESETS: readonly AccrualReviewPreset[] = [
    ACCRUAL_REVIEW_PRESET_RULE_SUGGESTED,
    ACCRUAL_REVIEW_PRESET_INVOICE,
    ACCRUAL_REVIEW_PRESET_BILL,
    ACCRUAL_REVIEW_PRESET_TRANSFER,
    ACCRUAL_REVIEW_PRESET_OTHER,
] as const;

export function isAccrualReviewPreset(
    value: string,
): value is AccrualReviewPreset {
    return ACCRUAL_REVIEW_PRESETS.some((p) => p === value);
}

/** Maps each accrual-review preset to its AccrualSuggestionBucket. */
export const ACCRUAL_REVIEW_PRESET_TO_BUCKET: Record<
    AccrualReviewPreset,
    AccrualSuggestionBucket
> = {
    [ACCRUAL_REVIEW_PRESET_RULE_SUGGESTED]:
        AccrualSuggestionBucket.RULE_SUGGESTED,
    [ACCRUAL_REVIEW_PRESET_INVOICE]: AccrualSuggestionBucket.INVOICE,
    [ACCRUAL_REVIEW_PRESET_BILL]: AccrualSuggestionBucket.BILL,
    [ACCRUAL_REVIEW_PRESET_TRANSFER]: AccrualSuggestionBucket.TRANSFER,
    [ACCRUAL_REVIEW_PRESET_OTHER]: AccrualSuggestionBucket.NONE,
};

export function findAccrualReviewPresetForBucket(
    bucket: AccrualSuggestionBucket,
): AccrualReviewPreset | null {
    return (
        ACCRUAL_REVIEW_PRESETS.find(
            (preset) => ACCRUAL_REVIEW_PRESET_TO_BUCKET[preset] === bucket,
        ) ?? null
    );
}

/** The full set of values that can appear in the task-type selector. */
export type TaskTypeOption = TaskType | TransactionPreset | AccrualReviewPreset;

const TASK_TYPE_VALUES: ReadonlySet<string> = new Set(Object.values(TaskType));

export function isTaskTypeOption(value: string): value is TaskTypeOption {
    return (
        TASK_TYPE_VALUES.has(value) ||
        isTransactionPreset(value) ||
        isAccrualReviewPreset(value)
    );
}

/**
 * Returns the underlying TaskType for a selector option.
 * The Uncategorized preset resolves to TaskType.TRANSACTION_REVIEW; the
 * remaining transaction presets resolve to TaskType.TRANSACTIONS;
 * accrual-review presets resolve to TaskType.ACCRUAL_REVIEW; other options
 * pass through.
 */
export function resolveTaskTypeFromOption(option: TaskTypeOption): TaskType {
    if (option === TRANSACTION_PRESET_UNCATEGORIZED) {
        return TaskType.TRANSACTION_REVIEW;
    }
    if (isTransactionPreset(option)) {
        return TaskType.TRANSACTIONS;
    }
    if (isAccrualReviewPreset(option)) {
        return TaskType.ACCRUAL_REVIEW;
    }
    return option;
}

const taskTypeOption = (
    type: TaskType,
): { value: TaskTypeOption; label: string } => ({
    value: type,
    label: getTaskTypeLabel(type) ?? type,
});

/** Grouped options for the task type selector in the add-task drawer. */
export const TASK_TYPE_OPTION_GROUPS: Array<{
    label: string | null;
    options: Array<{ value: TaskTypeOption; label: string }>;
}> = [
    {
        label: null,
        options: [taskTypeOption(TaskType.QUESTION)],
    },
    {
        label: "Documents",
        options: [
            taskTypeOption(TaskType.DOCUMENT_RETRIEVAL),
            taskTypeOption(TaskType.GET_DOCUMENTS),
            taskTypeOption(TaskType.FINANCIAL_DOCUMENTS),
        ],
    },
    {
        label: null,
        options: [taskTypeOption(TaskType.RECONCILIATION)],
    },
    {
        label: "Transactions",
        options: [
            taskTypeOption(TaskType.TRANSACTIONS),
            {
                value: TRANSACTION_PRESET_UNCATEGORIZED,
                label: "Uncategorized",
            },
            {
                value: TRANSACTION_PRESET_WITHOUT_COUNTERPARTY,
                label: "Without counterparty",
            },
            taskTypeOption(TaskType.TRANSACTION_REVIEW),
        ],
    },
    {
        label: "Accrual review",
        options: [
            {
                value: ACCRUAL_REVIEW_PRESET_RULE_SUGGESTED,
                label: "Confirm rule suggestions",
            },
            {
                value: ACCRUAL_REVIEW_PRESET_INVOICE,
                label: "Review invoice matches",
            },
            { value: ACCRUAL_REVIEW_PRESET_BILL, label: "Review bill matches" },
            {
                value: ACCRUAL_REVIEW_PRESET_TRANSFER,
                label: "Review transfer matches",
            },
            {
                value: ACCRUAL_REVIEW_PRESET_OTHER,
                label: "Review accrual transactions",
            },
        ],
    },
    {
        label: "Reports",
        options: [
            taskTypeOption(TaskType.PROFIT_LOSS),
            taskTypeOption(TaskType.BALANCE_SHEET),
        ],
    },
    {
        label: null,
        options: [taskTypeOption(TaskType.INVOICES)],
    },
    {
        label: "Counterparties",
        options: [
            taskTypeOption(TaskType.COUNTERPARTIES),
            taskTypeOption(TaskType.FORM_1099),
        ],
    },
    {
        label: "Bills",
        options: [taskTypeOption(TaskType.BILLS)],
    },
];

export const TASK_TYPE_LABEL: Partial<Record<TaskType, string>> =
    Object.fromEntries(
        TEMPLATE_ELIGIBLE_TASK_TYPES.flatMap((type) => {
            const label = getTaskTypeLabel(type);
            return label === null ? [] : [[type, label]];
        }),
    );

/** Labels for all task type selector options, including presets. Built from TASK_TYPE_OPTION_GROUPS so completeness is guaranteed by that source. */
export const ALL_TASK_TYPE_OPTION_LABELS: Partial<
    Record<TaskTypeOption, string>
> = Object.fromEntries(
    TASK_TYPE_OPTION_GROUPS.flatMap((group) =>
        group.options.map(({ value, label }) => [value, label]),
    ),
);

/** Sentinel select value standing in for a null assigneeRole ("No role"). */
export const NO_ASSIGNEE_ROLE_VALUE = "__no_role__";

export const ASSIGNEE_ROLE_LABEL: Record<TaskTemplateAssigneeRole, string> = {
    [TaskTemplateAssigneeRole.PREPARER]: "Preparer",
    [TaskTemplateAssigneeRole.REVIEWER]: "Reviewer",
    [TaskTemplateAssigneeRole.MANAGER]: "Manager",
    [TaskTemplateAssigneeRole.CLIENT]: "Client",
};

/** Label for a role select value, including the null ("No role") sentinel. */
export const assigneeRoleLabel = (
    role: TaskTemplateAssigneeRole | null,
): string => (role === null ? "No role" : ASSIGNEE_ROLE_LABEL[role]);

export const ASSIGNEE_ROLE_OPTIONS: TaskTemplateAssigneeRole[] = [
    TaskTemplateAssigneeRole.PREPARER,
    TaskTemplateAssigneeRole.REVIEWER,
    TaskTemplateAssigneeRole.MANAGER,
    TaskTemplateAssigneeRole.CLIENT,
];

export const TRANSACTIONS_VIEW_TYPE_OPTIONS: Array<{
    value: TransactionsViewType;
    label: string;
    description: string;
}> = [
    {
        value: "sticky",
        label: "Sticky",
        description:
            "Transactions are resolved at task creation and locked to that snapshot.",
    },
    {
        value: "dynamic",
        label: "Dynamic",
        description:
            "Transactions are resolved dynamically each time the task is opened.",
    },
];

export const TRANSACTIONS_VIEW_TYPE_LABEL: Record<
    TransactionsViewType,
    string
> = Object.fromEntries(
    TRANSACTIONS_VIEW_TYPE_OPTIONS.map(({ value, label }) => [value, label]),
) as Record<TransactionsViewType, string>;

export const stageKey = (stageId: string | null) => stageId ?? NO_STAGE_VALUE;

export const defaultView: View = {
    type: ViewType.TASK_GROUP,
    columns: [
        { id: CloseTemplateTaskColumn.DRAG_HANDLE },
        { id: CloseTemplateTaskColumn.NAME },
        { id: CloseTemplateTaskColumn.ASSIGNEE },
        { id: CloseTemplateTaskColumn.TASK_TYPE },
        { id: CloseTemplateTaskColumn.CLOSE_STAGE },
        { id: CloseTemplateTaskColumn.DEADLINE },
        { id: CloseTemplateTaskColumn.ESTIMATE },
        { id: CloseTemplateTaskColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: true,
        bulkActions: true,
        density: ViewDensity.MD,
    },
};

const CloseTemplateTaskColumnSchema = ColumnSchema.extend({
    id: z.nativeEnum(CloseTemplateTaskColumn),
});

export const CloseTemplateTasksViewSchema = ViewSchema.extend({
    type: z.literal(ViewType.TASK_GROUP),
    columns: z.array(CloseTemplateTaskColumnSchema),
});
